import type { CSSProperties, ReactNode } from 'react';
import SectionReveal from './sections/SectionReveal';
import { useReportsPrintStaticLayout } from './hooks/useReportsPrintStaticLayout.hook';

export interface HostedReportSectionLayoutProps {
  /** DOM id on the outer `<section>` (e.g. `engagement`). */
  sectionDomId: string;
  /** Section root class names (e.g. `py-16 md:py-24 px-6`). */
  className?: string;
  /** Section root inline styles. */
  style?: CSSProperties;
  /** Inner width wrapper class (default `max-w-5xl mx-auto`). */
  innerClassName?: string;
  /** Centered banner block; never scaled in print. */
  banner: ReactNode;
  /** Section body below the banner; may be scaled to fit one printed page. */
  children: ReactNode;
}

/**
 * Standard shell for hosted report sections: banner header + scalable body.
 * On the print route the banner skips Framer motion so icons are not clipped.
 * @param props - Section chrome and content.
 */
export const HostedReportSectionLayout = ({
  sectionDomId,
  className,
  style,
  banner,
  children,
  innerClassName = 'max-w-5xl mx-auto',
}: HostedReportSectionLayoutProps) => {
  const printStatic = useReportsPrintStaticLayout();

  return (
    <section
      id={sectionDomId}
      data-pdf-section
      className={className}
      style={style}
    >
      <div className={innerClassName}>
        {banner ? (
          printStatic ? (
            <div className="report-print-section__header">{banner}</div>
          ) : (
            <SectionReveal>{banner}</SectionReveal>
          )
        ) : null}
        <div className="report-section-print-scalable">{children}</div>
      </div>
    </section>
  );
};
